import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { BottomSheet, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { IconButton } from "../../../components/IconButton";
import { useRemoveFriend } from "../../hooks/useRemoveFriend";
import WorkWithFriendsContext from "../../WorkWithFriendsContext";
import Confirmation from "./Confirmation";

interface Props {
  onClose: () => void;
  modalState: UseModalState;
}
export default function RemoveFriendBottomSheet(props: Props) {
  const { onClose, modalState } = props;

  const { showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const { activeFriend } = useContext(WorkWithFriendsContext);

  const { mutate: removeFriend, isLoading: isLoadingRemoval } = useRemoveFriend({
    onSuccess: () => {
      if (isDefined(activeFriend)) {
        showSuccessToast(
          `${activeFriend.attributes.firstName} ${activeFriend.attributes.lastName} has been removed from your friends list`
        );
        void queryClient.invalidateQueries({ queryKey: ["friends-list"] });
      }

      onClose();
    },
    onError: () => {
      showErrorToast("Failed to remove friend. Please try again.");
    },
  });

  return (
    <BottomSheet modalState={modalState}>
      <Stack flex={1} flexDirection="column" p={6}>
        <Stack flex={1} alignItems="flex-end" p={4}>
          <IconButton
            variant="outlined"
            iconType="close"
            size="small"
            aria-label="close"
            onClick={onClose}
          />
        </Stack>
        <Confirmation
          pending={isLoadingRemoval}
          onConfirm={() => {
            if (isDefined(activeFriend)) {
              removeFriend(activeFriend.id);
            } else {
              onClose();
            }
          }}
          onCancel={() => {
            onClose();
          }}
        />
      </Stack>
    </BottomSheet>
  );
}
